import Link from 'next/link'
import type { Hit } from 'instantsearch.js'
import { isThisWeek, isThisMonth, isToday, isTomorrow } from 'date-fns'

import PostLabel from '@/components/ui/PostLabel'
import AppImage from '@/components/AppImage'
import { BookmarkAction, BookmarkButtonDashboard } from '@/components/post-actions/BookmarkAction'

import { decodeSafeEntities } from '@/lib/sanitise-strings'

export type GridArchiveItemProps = {
  hit: Hit
  size?: 'l'
}

function getDateInWords(date: Date): string {
  if (isToday(date)) {
    return 'Today'
  }
  if (isTomorrow(date)) {
    return 'Tomorrow'
  }
  if (isThisWeek(date)) {
    return 'This Week'
  }
  if (isThisMonth(date)) {
    return 'This Month'
  }

  return ''
}

export default function GridArchiveItem({ hit, size }: GridArchiveItemProps) {
  if (!hit.post_title || !hit.permalink) {
    return null
  }

  const imageUrl = hit?.images?.grid_image_url || null
  const imageAlt = hit?.images?.grid_image_alt || null

  const postId = parseInt(hit.objectID) || parseInt(hit.post_id) || null
  const postTitle = decodeSafeEntities(hit.post_title)
  const businessName = decodeSafeEntities(hit.business_info?.name)
  const location = decodeSafeEntities(hit?.location)

  const postDate = hit?.post_date_formatted_short
  const eventDate = hit?.event_date?.date_formatted_short

  const eventDateLabel = hit?.event_date?.start_date_unix
    ? new Date(hit.event_date.start_date_unix * 1000)
    : null
  const eventDateInWords = eventDateLabel ? getDateInWords(eventDateLabel) : null

  let sizes = '100vw'

  if (size === 'l') {
    sizes = '(min-width: 1920px) 828px, (min-width: 500px) 43.57vw, calc(95.56vw - 19px)'
  } else {
    sizes =
      '(min-width: 1940px) 544px, (min-width: 1300px) calc(25.65vw + 52px), (min-width: 500px) calc(46.41vw - 14px), calc(95.56vw - 19px)'
  }

  return (
    <article className="flex w-full flex-col justify-start gap-s duration-300 @container/grid-item hover:translate-y-2">
      <div className="gap-sn group relative flex flex-col">
        {imageUrl ? (
          <Link
            href={hit.permalink}
            aria-label={`view article ${postTitle}`}
            className="relative mb-s overflow-hidden bg-primary-25 duration-500 group-hover:text-primary">
            <AppImage
              path={imageUrl}
              alt={imageAlt || ''}
              height={237}
              width={396}
              loading="lazy"
              transforms={{
                width: '396',
                height: '237',
                focus: 'auto',
                dpr: 'auto',
                quality: 'auto',
              }}
              className="w-full object-contain"
              lqip={{
                active: false,
              }}
              sizes={sizes}
              srcSet={{
                396: { width: '396', height: '237' },
                606: { width: '606', height: '363' },
                828: { width: '828', height: '496' },
              }}
            />

            {eventDateInWords ? (
              <PostLabel className="absolute left-0 top-0 z-10 m-xs">{eventDateInWords}</PostLabel>
            ) : null}
          </Link>
        ) : null}
        <Link
          href={hit.permalink}
          className="h4 transition-color mb-0 duration-200 ease-in-out group-hover:text-primary">
          {postTitle}
        </Link>

        {postId ? (
          <div className="z-60 absolute right-0 top-0 m-8 bg-white">
            <BookmarkAction postId={postId}>
              <BookmarkButtonDashboard />
            </BookmarkAction>
          </div>
        ) : null}
      </div>

      {/* insights & news */}
      {hit.post_type === 'all-work' || hit.post_type === 'all-news' ? (
        <ul className="p-small w-full leading-tight text-grey-800">
          <li className="mr-xxxs inline">{businessName ? businessName : 'Jersey Finance'}</li>

          {postDate ? (
            <li className="inline">
              <span className="mr-xxxs">|</span>
              {postDate}
            </li>
          ) : null}
        </ul>
      ) : null}

      {/* events */}
      {hit.post_type === 'all-events' ? (
        <ul className="p-small w-full leading-tight text-grey-800">
          {eventDate ? <li className="mr-xxxs inline">{eventDate}</li> : null}

          {location ? (
            <li className="inline">
              {location && eventDate ? <span className="mr-xxxs">|</span> : null}
              {location}
            </li>
          ) : null}
        </ul>
      ) : null}
    </article>
  )
}
